import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import { formatQty, type VanTemplateProposal } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { fieldErrorOf, fieldErrorAt, numberTextIssue } from '../../lib/field-error';
import { isStockBusy } from '../../lib/refusal';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import {
  ErrorState,
  OfflineState,
  shownState,
  StaleNotice,
  refreshFailed,
} from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { Field, field } from '../create/Field';
import { IconInventory } from '../icons';
import { sourceLabel, useTemplateReview, VanTemplatePreview } from './VanTemplatePreview';
import { ReorderSuggestionsSheet } from './ReorderSuggestionsSheet';
import { VanTemplateSheet } from './VanTemplateSheet';

type Config = inferRouterOutputs<AppRouter>['restocks']['config'];
type Capture = Extract<VanTemplateProposal, { kind: 'configureVan' }>;
type Point = Config['points'][number];
type Row = Point & {
  isNew: boolean;
  keep: string;
  fill: string;
  pack: string;
  mode: 'keep' | 'edit' | 'reset' | 'stop';
};
const inputClass = `${field} min-h-11`;
const draftRow = (p: Point): Row => ({
  ...p,
  isNew: false,
  keep: formatQty(p.keepAtLeastHundredths),
  fill: typeof p.fillToHundredths === 'number' ? formatQty(p.fillToHundredths) : '',
  pack: p.reorderQtyHundredths === null ? '' : formatQty(p.reorderQtyHundredths),
  mode: 'keep',
});

/** Reconcile server capabilities separately from the person's retained capture. */
export function reconcileVanDraft(rows: Row[], next: Config): Row[] {
  return rows.map((row) => {
    const point = next.points.find((p) => p.itemId === row.itemId),
      choice = next.availableItems.find((i) => i.itemId === row.itemId);
    const metadata =
      point ?? (choice ? { ...choice, itemName: choice.name, available: true } : null);
    if (!metadata) return { ...row, available: false };
    return {
      ...row,
      ...metadata,
      keep: row.keep,
      fill: row.fill,
      pack: row.pack,
      isNew: row.isNew,
      mode:
        row.mode === 'keep' &&
        (row.pairAction !== metadata.pairAction || row.source !== metadata.source)
          ? 'edit'
          : row.mode,
    };
  });
}

export function VanPointsSheet({
  vanId,
  onClose,
  onBusyChange,
}: {
  vanId: string;
  onClose: () => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const trpc = useTRPC(),
    config = useQuery(trpc.restocks.config.queryOptions({ vanId }));
  const review = useTemplateReview(onClose);
  const [selection, setSelection] = useState(''),
    [template, setTemplate] = useState<string | null>(null),
    [childBusy, setChildBusy] = useState(false),
    [suggestions, setSuggestions] = useState(false),
    [editorEpoch, setEditorEpoch] = useState(0),
    [needsFresh, setNeedsFresh] = useState(false);
  const closeSuggestions = async () => {
    setChildBusy(true);
    setNeedsFresh(true);
    try {
      const fresh = await config.refetch();
      if (fresh.error || !fresh.data) return;
      setEditorEpoch((n) => n + 1);
      setNeedsFresh(false);
    } finally {
      setSuggestions(false);
      setChildBusy(false);
    }
  };
  useEffect(() => {
    onBusyChange?.(review.saving || childBusy);
    return () => onBusyChange?.(false);
  }, [review.saving, childBusy, onBusyChange]);
  const state = shownState(config, config.data),
    locked = review.saving || childBusy;
  return (
    <Sheet
      className="[&_button]:min-h-11 [&_button]:min-w-11"
      open
      wide
      onClose={() => {
        if (!locked) onClose();
      }}
      title={
        <span className="block whitespace-normal break-words">
          {config.data?.name ?? 'Van settings'}
        </span>
      }
    >
      {state === 'offline' ? (
        <OfflineState icon={IconInventory} what="van settings" />
      ) : state === 'loading' ? (
        <Skeleton className="h-40" />
      ) : state === 'error' ? (
        <ErrorState
          icon={IconInventory}
          what="van settings"
          onRetry={() => void config.refetch()}
        />
      ) : config.data ? (
        <>
          {!review.allowed ? (
            <Notice tone="danger" alert>
              Your role no longer permits policy changes. Your draft is kept.
            </Notice>
          ) : null}
          <StaleNotice
            show={refreshFailed(config, config.data)}
            onRetry={() => void config.refetch()}
          />
          <div hidden={!!review.state || suggestions} className="space-y-4">
            {config.data.templateId ? (
              <section className="space-y-2">
                <p>
                  Template:{' '}
                  <button
                    className="min-h-11 underline"
                    disabled={locked}
                    onClick={() => setTemplate(config.data!.templateId)}
                  >
                    {config.data.templateName ?? 'Unavailable template'}
                  </button>
                </p>
                <Button
                  variant="ghost"
                  disabled={locked || !review.online}
                  onClick={() => void review.start({ kind: 'detach', vanId })}
                >
                  Review detach
                </Button>
              </section>
            ) : (
              <p className="text-sm text-muted-foreground">No template assigned.</p>
            )}
            {needsFresh ? (
              <ErrorState
                icon={IconInventory}
                what="saved van settings after usage review"
                onRetry={() => void closeSuggestions()}
              />
            ) : (
              <VanPointsEditor
                key={editorEpoch}
                onSuggestions={() => setSuggestions(true)}
                config={config.data}
                online={review.online}
                saving={locked || !review.allowed}
                reviewing={!!review.state?.loading}
                active={!review.state}
                error={review.draftError}
                onReview={(p) => void review.start(p)}
                onReload={async () => {
                  const next = await config.refetch();
                  if (next.error) throw next.error;
                  if (!next.data) throw new Error('Settings did not load.');
                  return next.data;
                }}
              />
            )}
            <TemplateChoice
              selected={selection}
              onSelect={setSelection}
              disabled={locked || !review.online}
              onReview={() =>
                void review.start({
                  kind: 'apply',
                  vanId,
                  templateId: selection,
                  preserveLocalItemIds: [],
                })
              }
            />
          </div>
          {review.state ? (
            <VanTemplatePreview
              authorized={review.allowed}
              view={review.state.view}
              proposal={review.state.proposal}
              page={review.state.page}
              loading={review.state.loading}
              saving={review.saving}
              online={review.online}
              error={review.state.error}
              onConfirm={() => void review.confirm()}
              onPage={review.page}
              onPreserveLocal={review.preserve}
              onReload={review.reload}
              onBack={review.back}
            />
          ) : null}
          {suggestions ? (
            <ReorderSuggestionsSheet
              initialScope={{ kind: 'location', id: vanId }}
              onClose={() => void closeSuggestions()}
              onBusyChange={setChildBusy}
            />
          ) : null}
          {template ? (
            <VanTemplateSheet
              templateId={template}
              onClose={() => setTemplate(null)}
              onBusyChange={setChildBusy}
            />
          ) : null}
        </>
      ) : null}
    </Sheet>
  );
}

/** Catalogue paging is independent of the van draft; no source pair is calculated here. */
function TemplateChoice({
  selected,
  onSelect,
  disabled,
  onReview,
}: {
  selected: string;
  onSelect: (id: string) => void;
  disabled: boolean;
  onReview: () => void;
}) {
  const trpc = useTRPC(),
    [offset, setOffset] = useState(0),
    q = useQuery(trpc.restocks.templates.queryOptions({ offset }));
  return (
    <section className="space-y-2 border-t border-border pt-4">
      <Field label="Apply or switch template">
        <select
          className={inputClass}
          value={selected}
          disabled={disabled || q.isFetching}
          onChange={(e) => onSelect(e.target.value)}
        >
          <option value="">Choose a template</option>
          {selected && !q.data?.rows.some((r) => r.templateId === selected) ? (
            <option value={selected}>Selected template</option>
          ) : null}
          {q.data?.rows.map((r) => (
            <option key={r.templateId} value={r.templateId}>
              {r.name} · {r.partCount} parts
            </option>
          ))}
        </select>
      </Field>
      {q.error ? (
        <Notice tone="danger" alert>
          {q.error.message}
        </Notice>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          disabled={disabled || offset === 0 || q.isFetching}
          onClick={() => setOffset((n) => Math.max(0, n - 25))}
        >
          Previous templates
        </Button>
        <Button
          variant="ghost"
          disabled={disabled || q.data?.nextOffset == null || q.isFetching}
          onClick={() => setOffset(q.data!.nextOffset!)}
        >
          Next templates
        </Button>
        <Button disabled={disabled || !selected || !!q.error} onClick={onReview}>
          Review template
        </Button>
      </div>
    </section>
  );
}

/** Capture only: server actions decide Local/reset/stop semantics and server review resolves
 * every resulting pair, pack and quantity. Retain this mounted draft while reviewing. */
export function VanPointsEditor({
  config,
  online,
  saving,
  reviewing = false,
  error,
  onReview,
  onReload,
  onSuggestions,
  active = true,
}: {
  active?: boolean;
  config: Config;
  online: boolean;
  saving: boolean;
  reviewing?: boolean;
  error?: { message: string } | null;
  onReview: (input: Capture) => void;
  onReload: () => Promise<Config>;
  onSuggestions?: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const fieldsId = useId(),
    [base, setBase] = useState(config),
    [feeder, setFeeder] = useState(config.feederId ?? ''),
    [rows, setRows] = useState(() => config.points.map(draftRow));
  const [selection, setSelection] = useState(''),
    [reloadBusy, setReloadBusy] = useState(false),
    [localError, setLocalError] = useState<string | null>(null),
    [reloaded, setReloaded] = useState(false),
    [page, setPage] = useState(0);
  const [submitted, setSubmitted] = useState<Array<{ itemId: string; kind: string }>>([]);
  useEffect(() => {
    if (!active || !error) return;
    const index = submitted.findIndex((c, i) =>
      ['keep', 'fill', 'reorderQty'].some((key) =>
        fieldErrorAt(error.message, ['proposal', 'changes', i, key]),
      ),
    );
    if (index >= 0) {
      const row = rows.findIndex((r) => r.itemId === submitted[index]!.itemId);
      if (row >= 0) setPage(Math.floor(row / 100));
    }
    requestAnimationFrame(() =>
      root.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
    );
  }, [active, error]);
  const legacy =
    base.contractVersion !== 2 ||
    !('templateId' in base) ||
    base.points.some(
      (p) =>
        typeof p.fillToHundredths !== 'number' ||
        !['template', 'local', 'van_only', 'by_hand'].includes(p.source) ||
        !p.pairAction,
    );
  const badCapture = rows.some(
    (p) =>
      numberTextIssue(p.keep, 'Keep') ||
      numberTextIssue(p.fill, 'Fill') ||
      numberTextIssue(p.pack, 'Purchase pack'),
  );
  const busy = isStockBusy(error),
    locked = saving || reviewing || reloadBusy;
  const dirty =
    feeder !== (base.feederId ?? '') ||
    rows.length !== base.points.length ||
    rows.some((r) => {
      const p = base.points.find((p) => p.itemId === r.itemId);
      return (
        !p ||
        r.isNew ||
        r.mode !== 'keep' ||
        r.keep !== formatQty(p.keepAtLeastHundredths) ||
        r.fill !== formatQty(p.fillToHundredths) ||
        r.pack !==
          (p.reorderQtyHundredths === null ? '' : formatQty(p.reorderQtyHundredths))
      );
    });
  const change = (id: string, key: 'keep' | 'fill' | 'pack', value: string) =>
    setRows((r) =>
      r.map((p) =>
        p.itemId === id
          ? { ...p, [key]: value, ...(key === 'pack' ? {} : { mode: 'edit' as const }) }
          : p,
      ),
    );
  const reload = async () => {
    setReloadBusy(true);
    setLocalError(null);
    try {
      const next = await onReload();
      setBase(next);
      setRows((rows) => reconcileVanDraft(rows, next));
      setReloaded(true);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not refresh settings.');
    } finally {
      setReloadBusy(false);
    }
  };
  const submit = () => {
    if (!online || locked || legacy || badCapture || base.templateIssue) return;
    const changes: Capture['changes'] = [];
    for (const p of rows) {
      const old = base.points.find((o) => o.itemId === p.itemId);
      if (p.mode === 'reset') changes.push({ kind: 'reset', itemId: p.itemId });
      else if (p.mode === 'stop') {
        if (!p.isNew) changes.push({ kind: 'stopWatching', itemId: p.itemId });
      } else if (p.isNew || p.mode === 'edit') {
        if (!p.keep.trim() || !p.fill.trim()) {
          setLocalError('Enter Keep and Fill for every watched part.');
          setPage(Math.floor(rows.indexOf(p) / 100));
          return;
        }
        changes.push({
          kind: p.pairAction,
          itemId: p.itemId,
          keep: Number(p.keep),
          fill: Number(p.fill),
        });
      }
      const pack = p.pack.trim() ? Number(p.pack) : null;
      if (
        pack !==
        (old?.reorderQtyHundredths == null ? null : old.reorderQtyHundredths / 100)
      )
        changes.push({ kind: 'pack', itemId: p.itemId, reorderQty: pack });
    }
    setSubmitted(changes.map((c) => ({ itemId: c.itemId, kind: c.kind })));
    setLocalError(null);
    onReview({
      kind: 'configureVan',
      vanId: base.vanId,
      feederId: feeder || null,
      changes,
    });
  };
  return (
    <div ref={root} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {base.ownerName} · Watching parts never counts stock.
      </p>
      <p className="text-sm">
        {base.templateName ? `Template: ${base.templateName}` : 'By hand van policy'}
      </p>
      {!online ? (
        <Notice tone="warn">Offline. Go online to review; your inputs are kept.</Notice>
      ) : null}
      {error ? (
        <Notice tone="danger" alert>
          {fieldErrorOf(error.message)}
          {busy ? ' This attempt made no changes. Try saving again.' : ''}
        </Notice>
      ) : null}
      {localError ? (
        <Notice tone="danger" alert>
          {localError}
        </Notice>
      ) : null}
      {base.templateIssue ? (
        <Notice tone="danger" alert>
          {base.templateIssue}
        </Notice>
      ) : null}
      {legacy ? (
        <Notice tone="warn">
          Refresh saved settings before editing this older saved response.
        </Notice>
      ) : null}
      {onSuggestions ? (
        <section className="space-y-2">
          <Button
            variant="ghost"
            disabled={!online || locked || dirty || legacy}
            onClick={onSuggestions}
          >
            Review usage suggestions
          </Button>
          {dirty ? (
            <p role="status" className="text-sm">
              Save or undo your van edits before opening usage suggestions. Your draft is
              kept.
            </p>
          ) : null}
        </section>
      ) : null}
      <Button
        variant="ghost"
        className="min-h-11"
        disabled={!online || locked}
        onClick={() => void reload()}
      >
        {reloadBusy ? 'Reloading…' : 'Reload saved settings'}
      </Button>
      {reloaded ? (
        <section
          className="space-y-1 border border-border p-3 text-xs"
          aria-live="polite"
        >
          <p>
            Saved settings reloaded. Review these beside your retained draft before
            saving.
          </p>
          {base.points.map((p) => (
            <p key={p.itemId}>
              {p.itemName}: Keep {formatQty(p.keepAtLeastHundredths)} · Fill{' '}
              {formatQty(p.fillToHundredths)}
            </p>
          ))}
        </section>
      ) : null}
      {!legacy ? (
        <>
          <fieldset disabled={locked} className="min-w-0 space-y-4">
            <Field label="Feeder">
              <select
                className={inputClass}
                value={feeder}
                onChange={(e) => setFeeder(e.target.value)}
              >
                <option value="">Choose feeder</option>
                {feeder && !base.eligibleFeeders.some((f) => f.id === feeder) ? (
                  <option value={feeder}>Unavailable feeder — choose another</option>
                ) : null}
                {base.eligibleFeeders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </Field>
            {base.feederIssue ? <Notice tone="warn">{base.feederIssue}</Notice> : null}
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Watched parts</h2>
              {!rows.length ? (
                <p className="text-sm text-muted-foreground">
                  Nothing watched on this van yet.
                </p>
              ) : null}
              <ul className="divide-y divide-border">
                {rows.slice(page * 100, page * 100 + 100).map((p) => {
                  const actionIndex = submitted.length
                      ? submitted.findIndex(
                          (c) => c.itemId === p.itemId && c.kind !== 'pack',
                        )
                      : rows.indexOf(p),
                    packIndex = submitted.findIndex(
                      (c) => c.itemId === p.itemId && c.kind === 'pack',
                    );
                  const inputs = [
                    {
                      key: 'keep' as const,
                      label: 'Keep at least',
                      path: 'keep',
                      index: actionIndex,
                    },
                    {
                      key: 'fill' as const,
                      label: 'Fill to',
                      path: 'fill',
                      index: actionIndex,
                    },
                    {
                      key: 'pack' as const,
                      label: 'Purchase pack',
                      path: 'reorderQty',
                      index: packIndex,
                    },
                  ];
                  return (
                    <li key={p.itemId} className="space-y-2 py-3">
                      <p className="break-words text-sm font-semibold">{p.itemName}</p>
                      <p className="text-xs">
                        {sourceLabel(p.source)}
                        {p.mode === 'reset'
                          ? ' · Reset to template in review'
                          : p.mode === 'stop'
                            ? ' · Stop watching in review'
                            : p.mode === 'edit'
                              ? ' · Pair changed in draft'
                              : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.isNew ? (
                          'Review to load quantities for this part.'
                        ) : (
                          <>
                            On hand {formatQty(p.onHandHundredths)} · Incoming{' '}
                            {formatQty(p.onOrderHundredths)}
                          </>
                        )}
                      </p>
                      {!p.available ? (
                        <p className="text-xs">
                          Unavailable part. Keep saved demand unchanged or explicitly stop
                          watching where offered.
                        </p>
                      ) : null}
                      <div className="grid grid-cols-2 gap-2">
                        {inputs.map((c) => {
                          const issue =
                            numberTextIssue(p[c.key], c.label) ??
                            fieldErrorAt(error?.message, [
                              'proposal',
                              'changes',
                              c.index,
                              c.path,
                            ]);
                          const id = `${fieldsId}-${p.itemId}-${c.key}`;
                          return (
                            <Field key={c.key} label={c.label}>
                              <input
                                className={inputClass}
                                aria-label={`${c.label} for ${p.itemName}`}
                                required={c.key !== 'pack'}
                                aria-invalid={issue ? true : undefined}
                                aria-describedby={issue ? id : undefined}
                                inputMode="decimal"
                                value={p[c.key]}
                                disabled={
                                  c.key !== 'pack' &&
                                  (!p.available ||
                                    p.mode === 'reset' ||
                                    p.mode === 'stop')
                                }
                                onChange={(e) => change(p.itemId, c.key, e.target.value)}
                              />
                              {issue ? (
                                <p
                                  id={id}
                                  role="alert"
                                  className="text-xs text-[var(--sw-danger-text)]"
                                >
                                  {issue}
                                </p>
                              ) : null}
                            </Field>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {p.canReset ? (
                          <Button
                            variant="ghost"
                            className="min-h-11"
                            onClick={() =>
                              setRows((r) =>
                                r.map((x) =>
                                  x.itemId === p.itemId ? { ...x, mode: 'reset' } : x,
                                ),
                              )
                            }
                          >
                            Reset to template
                            {p.templatePair
                              ? ` · Keep ${formatQty(p.templatePair.keepAtLeastHundredths)} / Fill ${formatQty(p.templatePair.fillToHundredths)}`
                              : ''}
                          </Button>
                        ) : null}
                        {p.canStopWatching || p.isNew ? (
                          <Button
                            variant="ghost"
                            className="min-h-11"
                            aria-label={`Stop watching ${p.itemName}`}
                            onClick={() =>
                              setRows((r) =>
                                p.isNew
                                  ? r.filter((x) => x.itemId !== p.itemId)
                                  : r.map((x) =>
                                      x.itemId === p.itemId ? { ...x, mode: 'stop' } : x,
                                    ),
                              )
                            }
                          >
                            Stop watching
                          </Button>
                        ) : null}
                        {p.mode !== 'keep' ? (
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setRows((r) =>
                                r.map((x) =>
                                  x.itemId === p.itemId
                                    ? {
                                        ...x,
                                        mode: 'keep',
                                        ...(base.points.find((o) => o.itemId === x.itemId)
                                          ? draftRow(
                                              base.points.find(
                                                (o) => o.itemId === x.itemId,
                                              )!,
                                            )
                                          : {}),
                                      }
                                    : x,
                                ),
                              )
                            }
                          >
                            Undo pair change
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {rows.length > 100 ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    disabled={page === 0}
                    onClick={() => setPage((n) => n - 1)}
                  >
                    Previous parts
                  </Button>
                  <span>
                    {page + 1} / {Math.ceil(rows.length / 100)}
                  </span>
                  <Button
                    variant="ghost"
                    disabled={(page + 1) * 100 >= rows.length}
                    onClick={() => setPage((n) => n + 1)}
                  >
                    Next parts
                  </Button>
                </div>
              ) : null}
            </section>
            <Field label="Watch a part">
              <select
                className={inputClass}
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
              >
                <option value="">Choose a part</option>
                {base.availableItems
                  .filter((i) => !rows.some((p) => p.itemId === i.itemId))
                  .map((i) => (
                    <option key={i.itemId} value={i.itemId}>
                      {i.name}
                      {i.sku ? ` · ${i.sku}` : ''}
                    </option>
                  ))}
              </select>
            </Field>
            <Button
              variant="ghost"
              className="min-h-11"
              disabled={!selection}
              onClick={() => {
                const i = base.availableItems.find((i) => i.itemId === selection);
                if (!i) return;
                setRows((r) => [
                  ...r,
                  {
                    itemId: i.itemId,
                    itemName: i.name,
                    sku: i.sku,
                    unitLabel: i.unitLabel,
                    available: true,
                    source: i.source,
                    pairAction: i.pairAction,
                    canReset: i.canReset,
                    canStopWatching: i.canStopWatching,
                    templatePair: i.templatePair,
                    keepAtLeastHundredths: 0,
                    fillToHundredths: 0,
                    reorderQtyHundredths: i.reorderQtyHundredths,
                    onHandHundredths: 0,
                    onOrderHundredths: 0,
                    isNew: true,
                    keep: '',
                    fill: '',
                    pack:
                      i.reorderQtyHundredths === null
                        ? ''
                        : formatQty(i.reorderQtyHundredths),
                    mode: 'edit',
                  },
                ]);
                setPage(Math.floor(rows.length / 100));
                setSelection('');
              }}
            >
              Watch a part
            </Button>
          </fieldset>
          <section className="space-y-1 text-sm">
            <h2 className="font-semibold">Still owed</h2>
            {base.owed.length ? (
              base.owed.map((o) => (
                <p className="break-words" key={o.itemId}>
                  {o.itemName}: {formatQty(o.quantityHundredths)}
                </p>
              ))
            ) : (
              <p>Nothing owed.</p>
            )}
            <p className="text-xs text-muted-foreground">
              Changing watched parts leaves existing owed quantities in place.
            </p>
          </section>
        </>
      ) : null}
      <Button
        className="min-h-11 w-full"
        disabled={!online || locked || legacy || badCapture || !!base.templateIssue}
        onClick={submit}
      >
        {reviewing ? 'Reviewing…' : 'Review changes'}
      </Button>
    </div>
  );
}
